// Shared shipping policy helpers — single source of truth for the storefront.
// The server remains authoritative for what Stripe actually charges; these
// mirror the same rules so the UI can show accurate shipping + delivery dates.

export const SHIPPING_DEFAULTS = {
  handling_days: 2,
  delivery_min_days: 3,
  delivery_max_days: 8,
  base_price: 4.99,
  mode: "per_item", // "flat" | "per_item"
};

export const isDigitalFormat = (fmt) => {
  const f = (fmt || "").toLowerCase();
  return ["ebook", "e-book", "digital", "audiobook"].some((k) => f.includes(k));
};

/** Effective shipping price for one cart line: per-book override, else global default. */
export const effectiveShipPrice = (item, settings = SHIPPING_DEFAULTS) =>
  item.shipping_price != null ? Number(item.shipping_price) : Number(settings.base_price || 0);

/** Cart shipping total mirroring the server rules. Digital lines never ship. */
export const computeCartShipping = (items, settings = SHIPPING_DEFAULTS) => {
  const physical = (items || []).filter((i) => !isDigitalFormat(i.format));
  if (!physical.length) return 0;
  if (settings.mode === "flat") {
    let sum = 0;
    let needsBase = false;
    for (const i of physical) {
      if (i.shipping_price != null) sum += Number(i.shipping_price) * i.quantity;
      else needsBase = true;
    }
    return sum + (needsBase ? Number(settings.base_price || 0) : 0);
  }
  return physical.reduce((sum, i) => sum + effectiveShipPrice(i, settings) * i.quantity, 0);
};

/** Add N business days (skip Sat/Sun) to a date. */
export const addBusinessDays = (from, days) => {
  const d = new Date(from);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return d;
};

const fmtDate = (d) => d.toLocaleDateString("en-US", { month: "long", day: "numeric" });

/** "September 10 to September 15" from handling + transit window (business days). */
export const deliveryRangeText = (handlingDays, deliveryMinDays, deliveryMaxDays) => {
  const earliest = addBusinessDays(new Date(), Number(handlingDays) + Number(deliveryMinDays));
  const latest = addBusinessDays(new Date(), Number(handlingDays) + Number(deliveryMaxDays));
  return `${fmtDate(earliest)} to ${fmtDate(latest)}`;
};

/** Effective (handling, min, max) for a book given global settings. */
export const effectiveShipTimes = (book, settings = SHIPPING_DEFAULTS) => ({
  handling: book?.handling_days ?? settings.handling_days,
  min: book?.delivery_min_days ?? settings.delivery_min_days,
  max: book?.delivery_max_days ?? settings.delivery_max_days,
});
